// Board rendering and cell management
// Layout definitions (active 14×14 + legacy 13×13) live in @wordsheet/shared/board-layouts
import { createTileElement } from './tiles.js';
import {
    BOARD_LAYOUTS,
    ALL_BOARD_LAYOUTS,
    DEFAULT_BOARD_SIZE,
    getRandomLayoutName,
    isCenterCell
} from '@wordsheet/shared/board-layouts';

// Re-export for lobby/game code that imports via board.js
export { getRandomLayoutName, DEFAULT_BOARD_SIZE };

// Select a random layout (active 14×14 set only)
export function getRandomLayout() {
    const selectedName = getRandomLayoutName();
    console.log(`Board layout: ${selectedName}`);
    return { name: selectedName, layout: BOARD_LAYOUTS[selectedName] };
}

// Set layout by name (for loading saved games — includes legacy 13×13 layouts)
export function setLayoutByName(name) {
    if (ALL_BOARD_LAYOUTS[name]) {
        SPECIAL_SQUARES = ALL_BOARD_LAYOUTS[name];
        console.log(`Board layout set to: ${name}`);
        return true;
    }
    console.warn(`Unknown layout: ${name}, keeping current`);
    return false;
}

// Get current layout name
export function getLayoutName() {
    for (const [name, layout] of Object.entries(ALL_BOARD_LAYOUTS)) {
        if (layout === SPECIAL_SQUARES) return name;
    }
    return null;
}

// Current game's layout (selected once at load, can be reset)
const initialLayout = getRandomLayout();
export let SPECIAL_SQUARES = initialLayout.layout;

export class Board {
    constructor() {
        this.cells = [];
        this.size = SPECIAL_SQUARES.size || DEFAULT_BOARD_SIZE;
        this.element = document.getElementById('board');
        this.initBoard();
    }

    initBoard() {
        // Re-read the current layout — it may have changed via setLayoutByName()
        this.size = SPECIAL_SQUARES.size || DEFAULT_BOARD_SIZE;
        // Set --board-size on the game container so sibling elements
        // (e.g. the message bar) can match the board width too
        const sizeHost = this.element.closest('.game-container') || this.element;
        sizeHost.style.setProperty('--board-size', this.size);
        this.element.innerHTML = '';
        this.cells = [];

        for (let row = 0; row < this.size; row++) {
            this.cells[row] = [];
            for (let col = 0; col < this.size; col++) {
                const cell = this.createCell(row, col);
                this.cells[row][col] = {
                    element: cell,
                    tile: null,
                    locked: false,
                    specialType: this.getSpecialType(row, col)
                };
                this.element.appendChild(cell);
            }
        }
    }

    createCell(row, col) {
        const cell = document.createElement('div');
        cell.className = 'cell';
        cell.dataset.row = row;
        cell.dataset.col = col;

        const specialType = this.getSpecialType(row, col);

        if (this.isCenterCell(row, col)) {
            cell.classList.add('center');
            cell.textContent = '\u2605';
        } else if (specialType) {
            cell.classList.add(specialType);
            cell.textContent = this.getSpecialLabel(specialType);
        }

        return cell;
    }

    isCenterCell(row, col) {
        return isCenterCell(row, col, this.size);
    }

    getSpecialType(row, col) {
        for (const [type, positions] of Object.entries(SPECIAL_SQUARES.squares)) {
            if (positions.some(([r, c]) => r === row && c === col)) {
                return this.toCssClass(type);
            }
        }
        return null;
    }

    toCssClass(type) {
        return type.replace(/([A-Z])/g, '-$1').toLowerCase();
    }

    getSpecialLabel(type) {
        const labels = {
            'triple-word': 'TW',
            'double-word': 'DW',
            'triple-letter': 'TL',
            'double-letter': 'DL'
        };
        return labels[type] || '';
    }

    placeTile(row, col, tile, pending = false) {
        const cellData = this.cells[row][col];
        if (cellData.locked) return false;

        cellData.tile = tile;
        cellData.element.innerHTML = '';
        cellData.element.classList.add('has-tile');

        if (pending) {
            cellData.element.classList.add('pending');
        }

        // Pending tiles are draggable, locked tiles are not
        const tileElement = createTileElement(tile, pending);
        cellData.element.appendChild(tileElement);

        return true;
    }

    removeTile(row, col) {
        const cellData = this.cells[row][col];
        if (cellData.locked) return null;

        const tile = cellData.tile;
        cellData.tile = null;
        cellData.element.classList.remove('has-tile', 'pending');
        cellData.element.innerHTML = '';

        // Restore special square label
        const specialType = this.getSpecialType(row, col);
        if (this.isCenterCell(row, col)) {
            cellData.element.textContent = '\u2605';
        } else if (specialType) {
            cellData.element.textContent = this.getSpecialLabel(specialType);
        }

        return tile;
    }

    lockTiles() {
        for (let row = 0; row < this.size; row++) {
            for (let col = 0; col < this.size; col++) {
                const cellData = this.cells[row][col];
                if (cellData.tile && cellData.element.classList.contains('pending')) {
                    cellData.element.classList.remove('pending');
                    cellData.element.classList.add('locked');
                    cellData.locked = true;

                    // Disable dragging on locked tiles
                    const tileElement = cellData.element.querySelector('.tile');
                    if (tileElement) {
                        tileElement.draggable = false;
                    }
                }
            }
        }
    }

    getPendingTiles() {
        const pending = [];
        for (let row = 0; row < this.size; row++) {
            for (let col = 0; col < this.size; col++) {
                const cellData = this.cells[row][col];
                if (cellData.tile && cellData.element.classList.contains('pending')) {
                    pending.push({ row, col, tile: cellData.tile });
                }
            }
        }
        return pending;
    }

    getTileAt(row, col) {
        if (row < 0 || row >= this.size || col < 0 || col >= this.size) {
            return null;
        }
        return this.cells[row][col].tile;
    }

    isEmpty() {
        for (let row = 0; row < this.size; row++) {
            for (let col = 0; col < this.size; col++) {
                if (this.cells[row][col].tile) return false;
            }
        }
        return true;
    }

    hasLockedTiles() {
        for (let row = 0; row < this.size; row++) {
            for (let col = 0; col < this.size; col++) {
                if (this.cells[row][col].locked) return true;
            }
        }
        return false;
    }

    // Serialize locked tiles for Firestore storage
    toSnapshot() {
        const tiles = [];
        for (let row = 0; row < this.size; row++) {
            for (let col = 0; col < this.size; col++) {
                const cellData = this.cells[row][col];
                if (cellData.locked && cellData.tile) {
                    tiles.push({
                        row,
                        col,
                        letter: cellData.tile.letter,
                        points: cellData.tile.points,
                        isBlank: cellData.tile.isBlank,
                        assignedLetter: cellData.tile.assignedLetter || null,
                        id: cellData.tile.id
                    });
                }
            }
        }
        return tiles;
    }

    // Load board state from Firestore snapshot
    loadSnapshot(tiles) {
        // Clear the board first
        this.initBoard();

        // Place all tiles as locked
        for (const tileData of tiles) {
            const tile = {
                letter: tileData.letter,
                points: tileData.points,
                isBlank: tileData.isBlank,
                assignedLetter: tileData.assignedLetter,
                id: tileData.id
            };
            this.placeTile(tileData.row, tileData.col, tile, false);
            this.cells[tileData.row][tileData.col].locked = true;
            this.cells[tileData.row][tileData.col].element.classList.add('locked');
            this.cells[tileData.row][tileData.col].element.classList.remove('pending');

            // Disable dragging
            const tileElement = this.cells[tileData.row][tileData.col].element.querySelector('.tile');
            if (tileElement) {
                tileElement.draggable = false;
            }
        }
    }
}
